import classNames from 'classnames';
import {observer} from 'mobx-react';
import React from 'react';
import {X9Content, X9ContentSnapshot} from '../../../../../domain';
import {
  DragDropContext,
  Draggable,
  DraggableChildProvided,
  Droppable,
  DroppableChildProvided
} from '../../../../../lib/components/drag-n-drop';
import {ReactNodeGenerator} from '../../../../../lib/helpers';
import {X9ContentStateStore} from '../../common/X9ContentStateStore';
import {GroupingExerciseModel} from '../models';
import {GroupingExerciseStore} from './GroupingExerciseStore';
import {indexToDroppableId, itemIdToPosition, ungroupedDroppableId} from './utils';


function renderDraggable(itemText: string, provided: DraggableChildProvided): React.ReactNode {
  const classname = classNames('grouping-exercise draggable',
    {
      mirror: provided.isMirror,
      dragging: !provided.isMirror && provided.isDragging,
      default: !provided.isMirror && !provided.isDragging
    });
  return (
    <div className={classname}
         {...provided.draggableProps}
         {...provided.mirrorProps}>
      {itemText}
    </div>
  );
}

function draggable(itemId: string, itemText: string): React.ReactNode {
  return (
    <Draggable draggableId={itemId}>
      {
        (provided: DraggableChildProvided) =>
          renderDraggable(itemText, provided)
      }
    </Draggable>);
}

function* draggables(store: GroupingExerciseStore, provided: DroppableChildProvided): ReactNodeGenerator {
  const state = store.grexState !== null ? store.grexState : store.customGrexState;
  if (state === null)
    return;

  const groups = store.model.groups;
  const itemPositions = store.itemPositions;

  for (let itemId of state.itemOrder) {
    const isUsed = itemPositions[itemId] !== undefined;
    const position = itemIdToPosition(itemId);
    const itemText = groups[position.group].items[position.index];
    if (isUsed)
      yield(
        <div key={itemId} className="draggable-container used">
          <div className="grouping-exercise draggable">
            {itemText}
          </div>
        </div>
      );
    else
      yield (
        <div key={itemId} className="draggable-container">
          {draggable(itemId, itemText)}
        </div>
      );
  }
}

function* renderGroupItems(store: GroupingExerciseStore,
                           groupIndex: number): ReactNodeGenerator {
  const groups = store.model.groups;
  const itemIds = store.groupStates.length > 0 ? store.groupStates[groupIndex] : store.customGroupStates[groupIndex];
  const customItemsStates = store.customItemsStates;
  const itemStates = store.itemStates ? store.itemStates : customItemsStates;

  for (let itemId of itemIds) {
    const position = itemIdToPosition(itemId);
    const state = itemStates[itemId];

    const checked = state.checked;
    const correct = checked && position.group === groupIndex;
    const incorrect = checked && position.group !== groupIndex;

    const itemText = groups[position.group].items[position.index];

    if (correct) {
      yield (
        <div key={itemId} className="draggable-container checked correct">
          <div className="grouping-exercise draggable">
            {itemText}
          </div>
        </div>);
      continue;
    }

    const className = classNames('draggable-container', {
      checked: checked,
      incorrect: incorrect
    });
    yield (
      <div key={itemId} className={className}>
        {draggable(itemId, itemText)}
      </div>
    );
  }
}

function renderDroppableGroup(store: GroupingExerciseStore,
                              groupIndex: number,
                              provided: DroppableChildProvided): React.ReactNode {
  const group = store.model.groups[groupIndex];
  return (
    <div className="card grouping-exercise group"
         {...provided.droppableProps}>
      <div className="card-header group-label">
        {group.label}
      </div>
      <div className="card-body group-items">
        {Array.from(renderGroupItems(store, groupIndex))}
      </div>
    </div>
  );
}

function* renderDroppableGroups(store: GroupingExerciseStore): ReactNodeGenerator {
  const groups = store.model.groups;

  for (let index = 0; index < groups.length; index++) {
    yield (
      <div key={index} className="col">
        <Droppable droppableId={indexToDroppableId(index)}>
          {
            (provided: DroppableChildProvided) =>
              renderDroppableGroup(store, index, provided)
          }
        </Droppable>
      </div>);
  }
}

interface Props {
  x9: X9Content | X9ContentSnapshot,
  store: X9ContentStateStore,
  lessonType?: string
}

@observer
export class StudentGroupingExercise extends React.Component<Props> {
  readonly store: GroupingExerciseStore;

  constructor(props: Readonly<Props>) {
    super(props);
    const model = JSON.parse(this.props.x9.Contents) as GroupingExerciseModel;
    this.store = new GroupingExerciseStore(props.x9.Id, model, props.store);
  }

  render() {
    const store = this.store;

    return (
      <>
        <DragDropContext canDropOn={store.canDropOn}
                         onDragUpdate={store.onDragUpdate}
                         onDragEnd={store.onDragEnd}>
          <div className="row">
            <div className="col">
              <div className="grouping-exercise">
                <Droppable droppableId={ungroupedDroppableId}>
                  {
                    (provided: DroppableChildProvided) => (
                      <div className="draggables" {...provided.droppableProps}>
                        {Array.from(draggables(this.store, provided))}
                      </div>
                    )
                  }
                </Droppable>
              </div>
            </div>
          </div>

          <div className="row">
            {Array.from(renderDroppableGroups(this.store))}
          </div>

        </DragDropContext>
        {this.props.lessonType !== 'Examination' && (
          <div className="row justify-content-end">
            <div className="col-auto">
              <button type="button" className="btn btn-primary"
                      onClick={this.store.check}>
                Check
              </button>
            </div>
          </div>
        )}
      </>
    );
  }
}
